/**
 * Scenario tags. A tag that matches an exam's related scenario keys makes a
 * graded attempt at this scenario count as evidence for that exam, so those
 * tags are offered by name with the exams they feed.
 */

import { useState } from 'react';
import type { ProgressionAuthoringView } from './progressionTypes';

export interface TagsEditorProps {
  tags: string[];
  examTags: ProgressionAuthoringView['examTags'];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
}

export function TagsEditor({ tags, examTags, onChange, disabled }: TagsEditorProps) {
  const [entry, setEntry] = useState('');
  const add = (t: string) => {
    const v = t.trim();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setEntry('');
  };
  const examsFor = (t: string) => examTags.find((e) => e.tag === t)?.exams ?? [];
  return (
    <div className="space-y-2" data-testid="tags-editor">
      <div className="flex flex-wrap gap-2">
        {tags.length === 0 && <span className="text-xs text-gray-500">No tags.</span>}
        {tags.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-800">
            {t}
            {examsFor(t).length > 0 && <span className="text-blue-700">(exam: {examsFor(t).map((e) => e.label).join(', ')})</span>}
            <button type="button" aria-label={`Remove tag ${t}`} disabled={disabled} className="text-gray-500 hover:text-gray-800"
              onClick={() => onChange(tags.filter((x) => x !== t))}>×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input aria-label="New tag" className="rounded-md border border-gray-300 px-2 py-1 text-sm" value={entry} disabled={disabled}
          onChange={(e) => setEntry(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(entry); } }} />
        <button type="button" className="rounded border border-gray-300 px-2 py-0.5 text-xs" disabled={disabled || !entry.trim()} onClick={() => add(entry)}>Add tag</button>
        <select aria-label="Exam tag" className="rounded-md border border-gray-300 px-2 py-1 text-sm" value="" disabled={disabled}
          onChange={(e) => e.target.value && add(e.target.value)}>
          <option value="">Add an exam tag…</option>
          {examTags.filter((e) => !tags.includes(e.tag)).map((e) => (
            <option key={e.tag} value={e.tag}>{e.tag} — {e.exams.map((x) => x.label).join(', ')}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
